import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, 
  Users, 
  Award, 
  Target, 
  BookOpen, 
  Globe,
  CheckCircle,
  XCircle,
  ArrowRight,
  Video,
  MessageSquare,
  Lightbulb,
  TrendingUp,
  Heart,
  ExternalLink,
  Play,
  Search,
  Loader2,
  Filter,
  DollarSign
} from 'lucide-react';
import { useFormSubmit } from '../../hooks/useFormSubmit';

const DEFAULT_REGISTRO_CONFIG = {
  titulo: 'Registro al Conversatorio ODS/UNESCO',
  beneficios: {
    titulo: 'Beneficios por participar',
    items: [
      'Carta - Invitación individual como conferencista',
      'Constancia de participación individual como conferencista',
      'Membresía 2026 a la Red de Investigaciones sobre Educación en Latinoamérica, RIEL',
      'Difusión permanente a nivel global vía canal CENID TV – YouTube'
    ]
  },
  requisitos: {
    titulo: 'Los requisitos para participar por uno de los lugares disponibles son:',
    items: [
      'Cada participante deberá ser miembro del CENID a través de la Red de Investigaciones sobre Educación en Latinoamérica, RIEL (Tener cubierto su pago de membresía 2025). De no ser miembro se deberá cubrir dicho pago y se le otorgará carta membresía por el presente año',
      'Datos institucionales y personales de cada participante',
      'Envío de material (video). Ver especificaciones líneas abajo'
    ]
  },
  informes: {
    titulo: '+ Informes al correo:',
    emailFallback: 'catedraunitwin@cenid.org'
  }
};

const DEFAULT_ESPECIFICACIONES = {
  titulo: 'Especificaciones del material',
  items: [
    { label: 'Formato', value: '.MP4' },
    { label: 'Duración máxima', value: '5 minutos (con 5 min serán aproximadamente entre 300 y 500 Mb)' },
    { label: 'Resolución', value: '720 píxeles: 1280 x 720' }
  ]
};

const DEFAULT_FORMULARIO_SOLICITUD = {
  titulo: 'Formulario de solicitud de participación al conversatorio',
  descripcion: 'Los campos marcados con * son obligatorios',
  botonEnviar: 'Enviar solicitud',
  botonEnviando: 'Enviando...',
  mensajeExito: 'Solicitud enviada exitosamente. Nos pondremos en contacto contigo pronto.',
  mensajeExitoDetalle: 'Te contactaremos pronto.',
  campos: [
    {
      type: 'radio',
      name: 'tipoRegistro',
      label: 'Registrarse como:',
      required: true,
      defaultValue: 'docente',
      options: [
        { value: 'docente', label: 'Docente Investigador' },
        { value: 'grupo', label: 'Grupo de Investigación' }
      ]
    },
    { type: 'text', name: 'nombreCompleto', label: 'Nombre Completo', placeholder: 'Tu nombre completo', required: true, validationMessage: 'El nombre es requerido' },
    { type: 'email', name: 'email', label: 'Email', placeholder: 'tu@email.com', required: true, validationMessage: 'El email es requerido', invalidMessage: 'Email no válido' },
    { type: 'text', name: 'lineaInvestigacion', label: 'Línea de Investigación', placeholder: 'Tu línea de investigación', required: true, validationMessage: 'La línea de investigación es requerida' },
    { type: 'text', name: 'institucion', label: 'Institución', placeholder: 'Tu institución', required: true, validationMessage: 'La institución es requerida' },
    { type: 'textarea', name: 'mensaje', label: 'Tu mensaje', placeholder: 'Cuéntanos más sobre tu participación...', rows: 4 },
    { type: 'checkbox', name: 'aceptacion', label: 'Acepto* Con el envío de este formulario aceptas el tratamiento y gestión de los datos personales según nuestra Política de Privacidad*.', required: true, validationMessage: 'Debe aceptar la política de privacidad' }
  ]
};

const getInitialFormData = (fields = DEFAULT_FORMULARIO_SOLICITUD.campos) => {
  return fields.reduce((values, field) => {
    if (!field.name) return values;
    values[field.name] = field.type === 'checkbox' ? Boolean(field.defaultValue) : field.defaultValue || '';
    return values;
  }, {});
};

const Conversatorio = () => {
  const [conversatorioData, setConversatorioData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTematica, setFilterTematica] = useState('');
  const [filterPais, setFilterPais] = useState('');
  const [selectedAnio, setSelectedAnio] = useState(String(new Date().getFullYear()));
  
  // Form state management
  const [formData, setFormData] = useState(getInitialFormData());
  const [formErrors, setFormErrors] = useState({});
  
  const { loading: isSubmitting, success: isSuccess, error: submitError, submitForm, resetForm: resetFormApi } = useFormSubmit('conversatorio');

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [conversatorioResponse, generalesResponse] = await Promise.all([
          fetch(`/data/conversatorio.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);

        const [conversatorioJson, generalesJson] = await Promise.all([
          conversatorioResponse.json(),
          generalesResponse.json()
        ]);

        setConversatorioData(conversatorioJson);
        setDatosBancarios(generalesJson?.datosBancarios || null);
      } catch (error) {
        console.error('Error al cargar datos del conversatorio:', error);
      } finally {
        setIsLoadingData(false);
      }
    };
    
    cargarDatos();
  }, []);

  useEffect(() => {
    if (conversatorioData?.conversatorios?.length) {
      const aniosDisponibles = [...new Set(
        conversatorioData.conversatorios
          .map((c) => c.anio)
          .filter((anio) => anio && anio.toString().trim() !== '')
      )].map(String).sort((a, b) => {
        const anioA = parseInt(a, 10);
        const anioB = parseInt(b, 10);
        if (!Number.isNaN(anioA) && !Number.isNaN(anioB)) {
          return anioB - anioA;
        }
        return String(b).localeCompare(String(a));
      });

      const currentYear = String(new Date().getFullYear());

      // If current year is available, select it. Otherwise, pick the latest available year.
      if (aniosDisponibles.includes(currentYear)) {
        setSelectedAnio(currentYear);
      } else if (!aniosDisponibles.includes(selectedAnio)) {
        if (aniosDisponibles.length > 0) setSelectedAnio(String(aniosDisponibles[0]));
      }
    }
  }, [conversatorioData]);

  // Filtrar conversatorios
  const filteredConversatorios = conversatorioData?.conversatorios?.filter(conv => {
    const matchSearch = searchTerm === '' || 
      conv.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.conferencistas.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchTematica = filterTematica === '' || conv.tematica === filterTematica;
    const matchPais = filterPais === '' || conv.pais === filterPais;
    
    return matchSearch && matchTematica && matchPais;
  }) || [];

  const conversatoriosFiltradosPorAnio = selectedAnio
    ? filteredConversatorios.filter((conv) => String(conv.anio) === String(selectedAnio))
    : filteredConversatorios;

  // Obtener listas únicas de temáticas y países
  const tematicas = [...new Set(conversatorioData?.conversatorios?.map(c => c.tematica) || [])].sort();
  const paises = [...new Set(conversatorioData?.conversatorios?.map(c => c.pais) || [])].sort();

  const anios = [...new Set(conversatorioData?.conversatorios?.map((c) => c.anio) || [])]
    .filter((anio) => anio && anio.toString().trim() !== '')
    .map((anio) => String(anio))
    .sort((a, b) => {
      const anioA = parseInt(a, 10);
      const anioB = parseInt(b, 10);
      if (!Number.isNaN(anioA) && !Number.isNaN(anioB)) {
        return anioB - anioA;
      }
      return b.localeCompare(a);
    });

  const costoMonto = Number(conversatorioData?.costoParticipacion?.monto);
  const costoMoneda = conversatorioData?.costoParticipacion?.moneda || 'MXN';
  const costoDescripcion = conversatorioData?.costoParticipacion?.descripcion || 'Costo por participante';
  const registroConfig = conversatorioData?.registro || DEFAULT_REGISTRO_CONFIG;
  const beneficios = registroConfig?.beneficios?.items || DEFAULT_REGISTRO_CONFIG.beneficios.items;
  const requisitos = registroConfig?.requisitos?.items || DEFAULT_REGISTRO_CONFIG.requisitos.items;
  const informesConfig = registroConfig?.informes || DEFAULT_REGISTRO_CONFIG.informes;
  const especificacionesConfig = conversatorioData?.especificacionesMaterial || DEFAULT_ESPECIFICACIONES;
  const especificaciones = especificacionesConfig?.items || DEFAULT_ESPECIFICACIONES.items;
  const formularioConfig = conversatorioData?.formularioSolicitud || DEFAULT_FORMULARIO_SOLICITUD;
  const formFields = formularioConfig?.campos?.length ? formularioConfig.campos : DEFAULT_FORMULARIO_SOLICITUD.campos;
  const informesEmail = conversatorioData?.contacto?.email || informesConfig.emailFallback || 'catedraunitwin@cenid.org';

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpiar error del campo al escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    formFields.forEach((field) => {
      if (!field.name || !field.required) return;

      const value = formData[field.name];
      if (field.type === 'checkbox') {
        if (!value) errors[field.name] = field.validationMessage || 'Este campo es requerido';
        return;
      }

      if (!String(value || '').trim()) {
        errors[field.name] = field.validationMessage || 'Este campo es requerido';
      }
    });

    const emailField = formFields.find((field) => field.name === 'email');
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = emailField?.invalidMessage || 'Email no válido';
    }

    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    await submitForm(formData);
  };

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData(formFields));
    setFormErrors({});
    resetFormApi();
  }, [formFields, resetFormApi]);

  // Auto reset después de éxito
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        resetForm();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, resetForm]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <MessageSquare className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Conversatorios ODS/UNESCO
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-6">
                Debates y discusiones sobre los Objetivos de Desarrollo Sostenible
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm md:text-base">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Video className="w-5 h-5" />
                  <span>Virtual y en diferido</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Globe className="w-5 h-5" />
                  <span>Cátedras UNESCO Iberoamericanas</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Logos */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-200 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0077C8]/5 via-transparent to-[#0b742f]/5"></div>
            <div className="relative flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
              {/* Logo Proyecto Cátedra */}
              <div className="group flex flex-col items-center">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform group-hover:scale-105 border-2 border-transparent hover:border-[#0b742f]/20">
                  <img 
                    src="https://recursos.cenid.org/img_cenid/proyecto_catedra.png" 
                    alt="Proyecto Cátedra" 
                    className="h-24 md:h-32 w-auto object-contain"
                  />
                </div>
                <span className="mt-3 text-sm font-semibold text-gray-600 group-hover:text-[#0b742f] transition-colors">Postulación Cátedra</span>
              </div>
            </div>
          </div>
        </section>

        {/* ¿Qué es? */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#0b742f]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <MessageSquare className="w-8 h-8 text-[#0b742f]" />
                ¿Qué es el Conversatorio ODS/UNESCO?
              </h2>
              <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
            </div>
            <p className="text-gray-700 leading-relaxed text-justify">
              El conversatorio sobre los Objetivos de Desarrollo Sostenible de la UNESCO son debates y discusiones en modalidad virtual y en diferido, organizados por el Centro de Estudios e Investigaciones para el Desarrollo Docente CENID con el fin de contribuir a la postulación del Proyecto Cátedra sobre análisis e inclusión del conocimiento.
            </p>
          </div>
        </section>

        {/* Propósito */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-[#e87729]">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                <Target className="w-8 h-8 text-[#e87729]" />
                Propósito principal
              </h2>
              <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
            </div>
            <p className="text-gray-700 leading-relaxed text-justify">
              El propósito principal del conversatorio es constituir un espacio para promover sinergias que faciliten la colaboración activa y movilizar la inteligencia colectiva y la experiencia de las Cátedras UNESCO universitarias, con el fin de intercambiar ideas sobre temas emergentes en el ámbito de la educación superior.
            </p>
          </div>
        </section>

        {/* Objetivos */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Lightbulb className="w-8 h-8 text-[#0b742f]" />
                  Objetivos del Conversatorio
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>

              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-[#0b742f] p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-[#0b742f]" />
                    Expansión del conocimiento
                  </h3>
                  <p className="text-gray-700">
                    A través de debates interdisciplinarios y multidisciplinarios, se busca expandir el conocimiento sobre cuestiones clave relacionadas con el mandato de la UNESCO en educación, cultura, comunicación y ciencias, así como los temas más amplios que son de interés para las Naciones Unidas, basándose en la experiencia e impacto de las Cátedras UNESCO de Iberoamérica en relación con la Agenda 2030 y los Objetivos de Desarrollo Sostenible (ODS).
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                    Fortalecimiento regional
                  </h3>
                  <p className="text-gray-700">
                    Este encuentro tiene como meta apoyar y fortalecer la capacidad de acción en la región Iberoamericana, promoviendo la inclusión, integración y complementariedad de iniciativas internacionales de intercambio de conocimiento.
                  </p>
                </div>

                <div className="bg-orange-50 border-l-4 border-[#e87729] p-6 rounded-r-lg">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Heart className="w-6 h-6 text-[#e87729]" />
                    Difusión de resultados
                  </h3>
                  <p className="text-gray-700">
                    Somos sabedores de que todo trabajo de investigación debe incluir un componente de difusión y comunicación de los resultados de la investigación. Ambos, buscando lograr mediante un diálogo directo y claro que el público en general comprenda y logre beneficios de los resultados del proceso investigativo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Participación */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg shadow-xl p-8">
            <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
              <Users className="w-8 h-8" />
              Participación de las Cátedras
            </h2>
            <p className="text-lg text-white/90">
              En cada área de conocimiento del Conversatorio se contará con la participación de representantes de Cátedras UNESCO en Iberoamérica, quienes expondrán sus posturas y reflexiones.
            </p>
          </div>
        </section>

        {/* Costo y cuenta bancaria */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-[#0b742f]" />
                Costo de participacion y cuenta bancaria
              </h2>
              <div className="h-1 w-32 bg-[#e87729] rounded-full mb-6"></div>

              <div className="bg-gradient-to-r from-green-50 to-white border-l-4 border-[#0b742f] rounded-r-lg p-6">
                <div className="mb-5">
                  <p className="text-sm text-gray-600 uppercase tracking-wide font-semibold">{costoDescripcion}</p>
                  <p className="text-3xl font-bold text-[#0b742f]">
                    {Number.isFinite(costoMonto) ? `$${costoMonto.toLocaleString('es-MX')}` : 'Por confirmar'}{' '}
                    <span className="text-lg font-semibold text-gray-700">{costoMoneda}</span>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Beneficiario</p>
                    <p className="text-sm font-bold text-gray-900">{datosBancarios?.titular || 'Centro de Estudios e Investigaciones para el Desarrollo Docente'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Banco</p>
                    <p className="text-sm font-bold text-gray-900">{datosBancarios?.banco || 'SANTANDER'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Cuenta</p>
                    <p className="text-base font-bold text-gray-900 tracking-wide">{datosBancarios?.numeroCuenta || '65-50495250-6'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-xs text-green-700 uppercase font-semibold mb-1">CLABE Interbancaria</p>
                    <p className="text-base font-bold text-green-900 tracking-wider">{datosBancarios?.clabe || '014320655049525065'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Registro al Conversatorio */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Award className="w-8 h-8 text-[#0b742f]" />
                  {registroConfig.titulo}
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-[#0b742f]" />
                  {registroConfig?.beneficios?.titulo || DEFAULT_REGISTRO_CONFIG.beneficios.titulo}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {beneficios.map((beneficio, index) => (
                    <div key={index} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg hover:bg-[#0b742f]/5 transition-colors duration-200">
                      <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{beneficio}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-[#e87729]" />
                  {registroConfig?.requisitos?.titulo || DEFAULT_REGISTRO_CONFIG.requisitos.titulo}
                </h3>
                <div className="space-y-4">
                  {requisitos.map((requisito, index) => (
                    <div key={index} className="flex items-start gap-3 bg-orange-50 p-4 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-[#e87729] text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <span className="text-gray-700 flex-1">{requisito}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg p-6">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-6 h-6" />
                  <div>
                    <p className="font-semibold mb-1">{informesConfig.titulo}</p>
                    {isLoadingData ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <a 
                        href={`mailto:${informesEmail}`}
                        className="text-white hover:text-gray-200 font-bold text-lg flex items-center gap-2"
                      >
                        {informesEmail}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Especificaciones del material */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-blue-500">
            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Video className="w-8 h-8 text-blue-600" />
                  {especificacionesConfig.titulo}
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="space-y-3">
                  {especificaciones.map((spec, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <span className="font-bold text-gray-900">{spec.label}:</span>{' '}
                        <span className="text-gray-700">{spec.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Formulario de solicitud */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#0b742f]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <MessageSquare className="w-8 h-8 text-[#0b742f]" />
                  {formularioConfig.titulo}
                </h2>
                <div className="h-1 w-32 bg-[#e87729] rounded-full"></div>
                {formularioConfig.descripcion && (
                  <p className="text-sm text-gray-600 mt-4">{formularioConfig.descripcion}</p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">

                {isSuccess && (
                  <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <p className="text-green-700 font-semibold">
                      ✓ {formularioConfig.mensajeExito || DEFAULT_FORMULARIO_SOLICITUD.mensajeExito}
                    </p>
                  </div>
                )}

                <form onSubmit={onSubmit} className="space-y-6">
                  {formFields.map((field) => {
                    const requiredLabel = field.required ? ' *' : '';

                    if (field.type === 'radio') {
                      return (
                        <div key={field.name}>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            {field.label}{requiredLabel}
                          </label>
                          <div className="space-y-2">
                            {(field.options || []).map((option) => (
                              <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                                <input 
                                  type="radio" 
                                  name={field.name} 
                                  value={option.value}
                                  onChange={handleChange}
                                  checked={formData[field.name] === option.value}
                                  className="w-4 h-4 text-[#0b742f] focus:ring-[#0b742f]"
                                  required={field.required}
                                />
                                <span className="text-gray-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                          {formErrors[field.name] && <p className="text-red-500 text-sm mt-1">{formErrors[field.name]}</p>}
                        </div>
                      );
                    }

                    if (field.type === 'textarea') {
                      return (
                        <div key={field.name}>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {field.label}{requiredLabel}
                          </label>
                          <textarea
                            name={field.name}
                            value={formData[field.name] || ''}
                            onChange={handleChange}
                            rows={field.rows || 4}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none resize-none"
                            placeholder={field.placeholder || ''}
                            required={field.required}
                          ></textarea>
                          {formErrors[field.name] && <p className="text-red-500 text-sm mt-1">{formErrors[field.name]}</p>}
                        </div>
                      );
                    }

                    if (field.type === 'checkbox') {
                      return (
                        <div key={field.name}>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input 
                              type="checkbox" 
                              name={field.name}
                              checked={Boolean(formData[field.name])}
                              onChange={handleChange}
                              className="w-5 h-5 text-[#0b742f] focus:ring-[#0b742f] rounded mt-1"
                              required={field.required}
                            />
                            <span className="text-sm text-gray-700">{field.label}</span>
                          </label>
                          {formErrors[field.name] && <p className="text-red-500 text-sm mt-1">{formErrors[field.name]}</p>}
                        </div>
                      );
                    }

                    return (
                      <div key={field.name}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {field.label}{requiredLabel}
                        </label>
                        <input
                          type={field.type || 'text'}
                          name={field.name}
                          value={formData[field.name] || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors duration-200 outline-none"
                          placeholder={field.placeholder || ''}
                          required={field.required}
                        />
                        {formErrors[field.name] && <p className="text-red-500 text-sm mt-1">{formErrors[field.name]}</p>}
                      </div>
                    );
                  })}

                  {/* Mensajes de éxito y error */}
                  {isSuccess && (
                    <div className="p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        <p className="font-semibold">¡Solicitud enviada exitosamente!</p>
                      </div>
                      <p className="text-sm mt-1">{formularioConfig.mensajeExitoDetalle || DEFAULT_FORMULARIO_SOLICITUD.mensajeExitoDetalle}</p>
                    </div>
                  )}

                  {submitError && (
                    <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5" />
                        <p className="font-semibold">Error al enviar</p>
                      </div>
                      <p className="text-sm mt-1">{submitError}</p>
                    </div>
                  )}

                  {/* Botón de envío */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {formularioConfig.botonEnviando || DEFAULT_FORMULARIO_SOLICITUD.botonEnviando}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        {formularioConfig.botonEnviar || DEFAULT_FORMULARIO_SOLICITUD.botonEnviar}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Sala de Conversatorios */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-[#e87729]">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                  <Video className="w-8 h-8 text-[#e87729]" />
                  Sala de Conversatorios
                </h2>
                <div className="h-1 w-32 bg-[#0b742f] rounded-full"></div>
                <p className="text-gray-600 mt-4">
                  Accede a las grabaciones de los conversatorios realizados por diferentes Cátedras UNESCO
                </p>
              </div>

              {/* Buscador y Filtros */}
              <div className="mb-6 space-y-4">
                {/* Barra de búsqueda */}
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por título o conferencista..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-[#e87729]/30 rounded-lg focus:outline-none focus:border-[#e87729] transition-colors"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Filtros */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filtrar por temática
                    </label>
                    <select
                      value={filterTematica}
                      onChange={(e) => setFilterTematica(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#e87729]/30 rounded-lg focus:outline-none focus:border-[#e87729] transition-colors"
                    >
                      <option value="">Todas las temáticas</option>
                      {tematicas.map(tematica => (
                        <option key={tematica} value={tematica}>{tematica}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Filtrar por año
                    </label>
                    <select
                      value={selectedAnio}
                      onChange={(e) => setSelectedAnio(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#e87729]/30 rounded-lg focus:outline-none focus:border-[#e87729] transition-colors"
                    >
                      <option value="">Todos los años</option>
                      {anios.map((anio) => (
                        <option key={anio} value={anio}>{anio}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filtrar por país
                    </label>
                    <select
                      value={filterPais}
                      onChange={(e) => setFilterPais(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-[#e87729]/30 rounded-lg focus:outline-none focus:border-[#e87729] transition-colors"
                    >
                      <option value="">Todos los países</option>
                      {paises.map(pais => (
                        <option key={pais} value={pais}>{pais}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contador de resultados */}
                <div className="flex items-center justify-between bg-orange-50 px-4 py-2 rounded-lg">
                  <p className="text-sm text-gray-700">
                    Mostrando <strong>{conversatoriosFiltradosPorAnio.length}</strong> de{' '}
                    <strong>{filteredConversatorios.length}</strong> conversatorios
                    {selectedAnio ? ` en ${selectedAnio}` : ''}
                  </p>
                  {(searchTerm || filterTematica || filterPais) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterTematica('');
                        setFilterPais('');
                        setSelectedAnio('');
                      }}
                      className="text-sm text-[#e87729] hover:text-[#d87020] font-medium"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de conversatorios */}
              {isLoadingData ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 text-[#e87729] animate-spin" />
                </div>
              ) : conversatoriosFiltradosPorAnio.length > 0 ? (
                <div className="space-y-6">
                  {selectedAnio && (
                    <div className="flex items-center gap-3">
                      <div className="h-1 w-10 bg-[#e87729] rounded-full"></div>
                      <h3 className="text-2xl font-bold text-gray-900">{selectedAnio}</h3>
                    </div>
                  )}
                  {conversatoriosFiltradosPorAnio.map((conv) => (
                    <div 
                      key={conv.id}
                      className="bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg hover:border-[#0b742f] transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] p-3 rounded-lg">
                            <Play className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">
                            {conv.titulo}
                          </h4>
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              <strong>Conferencistas:</strong> {conv.conferencistas.join(', ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {conv.tematica}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {conv.pais}
                            </span>
                          </div>
                          <a 
                            href={conv.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#0b742f] hover:text-[#085a24] font-semibold transition-colors duration-200"
                          >
                            Ver conversatorio
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No se encontraron conversatorios con los filtros seleccionados</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterTematica('');
                      setFilterPais('');
                    }}
                    className="mt-4 text-[#e87729] hover:text-[#d87020] font-medium"
                  >
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Llamada a la Acción Final */}
        <section>
          <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
            <MessageSquare className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Únete a los Conversatorios ODS/UNESCO
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Participa en los debates sobre los Objetivos de Desarrollo Sostenible y contribuye al intercambio de conocimiento entre las Cátedras UNESCO de Iberoamérica.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-white text-[#0b742f] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-200 flex items-center gap-2">
                Registrarme
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-bold hover:bg-white/20 transition-colors duration-200 flex items-center gap-2">
                <Video className="w-5 h-5" />
                Ver conversatorios
              </button>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Conversatorio;
